import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
/* eslint-disable max-len */
import { PassengerTypeKey } from 'Models/Enums';

export interface IPassengerTypeEntityAttributes extends IModelAttributes {
	order: number;
	singularName: string;
	pluralName: string;
	quantity: number;
	abbreviation: string;
	description: string;
	tooltipInfo: string;
	tooltipEnabled: boolean;
	disabled: boolean;
	key: string;

	passengerDetails: Array<Models.PassengerDetailEntity | Models.IPassengerDetailEntityAttributes>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	canBeDriver: boolean;
}
@entity('PassengerTypeEntity', 'Passenger Type')
export default class PassengerTypeEntity extends Model implements IPassengerTypeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * This determines the order in which it appears in a list of passenger types.
	 */
	@observable
	@attribute<PassengerTypeEntity, number>()
	public order: number;

	/**
	 * The passenger name used when referenced as a singular item.
	 */
	@observable
	@attribute<PassengerTypeEntity, string>()
	public singularName: string;

	/**
	 * The passenger name used when referenced as a multiple items. Fallback should use the singular name.
	 */
	@observable
	@attribute<PassengerTypeEntity, string>()
	public pluralName: string;

	/**
	 * The number of passengers per passenger type.
	 */
	@observable
	@attribute<PassengerTypeEntity, number>()
	public quantity: number;

	/**
	 * The short-hand naming convention for this passenger type.
	 */
	@observable
	@attribute<PassengerTypeEntity, string>()
	public abbreviation: string;

	/**
	 * This appears as the second line in the counter when adding/editing a booking.
	 */
	@observable
	@attribute<PassengerTypeEntity, string>()
	public description: string;

	/**
	 * The text that appears in the tooltip.
	 */
	@observable
	@attribute<PassengerTypeEntity, string>()
	public tooltipInfo: string;

	/**
	 * True displays the tooltip for the passenger type. False hides it.
	 */
	@observable
	@attribute<PassengerTypeEntity, boolean>()
	public tooltipEnabled: boolean;

	/**
	 * True will hide this passenger type throughout the application.
	 */
	@observable
	@attribute<PassengerTypeEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<PassengerTypeEntity, string>()
	public key: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public passengerDetails: Models.PassengerDetailEntity[] = [];

	/**
	 * This is used to determine if the `disabled` attribute has changed.
	 */
	public readonly disabledSnapshot: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	@observable
	@attribute<PassengerTypeEntity, boolean>()
	public canBeDriver: boolean;

	constructor(attributes?: Partial<IPassengerTypeEntityAttributes>) {
		super(attributes);

		this.passengerDetails = [];
		this.prices = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IPassengerTypeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.order !== undefined) {
				this.order = attributes.order;
			}
			if (attributes.singularName !== undefined) {
				this.singularName = attributes.singularName;
			}
			if (attributes.pluralName !== undefined) {
				this.pluralName = attributes.pluralName;
			}
			if (attributes.quantity !== undefined) {
				this.quantity = attributes.quantity;
			}
			if (attributes.abbreviation !== undefined) {
				this.abbreviation = attributes.abbreviation;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.tooltipInfo !== undefined) {
				this.tooltipInfo = attributes.tooltipInfo;
			}
			if (attributes.tooltipEnabled !== undefined) {
				this.tooltipEnabled = attributes.tooltipEnabled;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.key !== undefined) {
				this.key = attributes.key;
			}
			if (isNotNullOrUndefined(attributes.canBeDriver)) {
				this.canBeDriver = attributes.canBeDriver;
			}
			if (attributes.passengerDetails !== undefined && Array.isArray(attributes.passengerDetails)) {
				if (!this.passengerDetails) {
					this.passengerDetails = [];
				}
				for (const model of attributes.passengerDetails) {
					if (model instanceof Models.PassengerDetailEntity) {
						this.passengerDetails.push(model);
					} else {
						this.passengerDetails.push(new Models.PassengerDetailEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
		}
	}

	public get passengerTypeKey(): PassengerTypeKey {
		switch (this.key) {
			case 'A':
				return 'A';
			case 'B':
				return 'B';
			case 'C':
				return 'C';
			case 'D':
				return 'D';
			case 'E':
				return 'E';
			case 'F':
				return 'F';
			case 'G':
				return 'G';
			default:
				return 'H';
		}
	}
}
